import { useCallback } from 'react';
/** moderator/transcriptions — HOOKS. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { transcriptionsApi } from './api';
import { transcriptionsKeys } from './keys';
import type { MatchEntry, TeamEntry, WorldEntry } from './types';

export function useTranscriptionWorlds() {
  return useQuery<WorldEntry[]>({
    queryKey: transcriptionsKeys.worlds(),
    queryFn: () => transcriptionsApi.listWorlds(),
  });
}

export function useTranscriptionTeams(gameName: string | null, worldName: string | null) {
  return useQuery<TeamEntry[]>({
    queryKey: transcriptionsKeys.teams(gameName, worldName),
    queryFn: () => transcriptionsApi.listTeams(gameName!, worldName!),
    enabled: !!gameName && !!worldName,
  });
}

export function useTranscriptionMatches(gameName: string | null, worldName: string | null, cohortId: string | null) {
  return useQuery<MatchEntry[]>({
    queryKey: transcriptionsKeys.matches(gameName, worldName, cohortId),
    queryFn: () => transcriptionsApi.listMatches(gameName!, worldName!, cohortId!),
    enabled: !!gameName && !!worldName && !!cohortId,
  });
}

export function useDeleteTranscriptionWorld() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { gameName: string; worldName: string }) => transcriptionsApi.deleteWorld(v.gameName, v.worldName),
    onSuccess: () => qc.invalidateQueries({ queryKey: transcriptionsKeys.all() }),
  });
}

/** Read a saved transcript on demand; keep transport and cache identity together. */
export function useReadTranscriptionFile() {
  const qc = useQueryClient();
  return useCallback((...args: Parameters<typeof transcriptionsApi.getFile>) => qc.fetchQuery({
    queryKey: transcriptionsKeys.file(...args),
    queryFn: () => transcriptionsApi.getFile(...args),
  }), [qc]);
}
